import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import DownloadIcon from '@mui/icons-material/Download';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import type { MetaRegistryEntry } from '../../types/systems';

interface ToolbarBcmProps {
  onAdd?: () => void;
  onSync?: () => void;
  onExportPng?: () => void;
  maps?: MetaRegistryEntry[];
  selectedMapId?: string;
  onSelectMap?: (mapEntryId: string) => void;
  onCreateMap?: () => void;
  creatingMap?: boolean;
  title?: string;
  modeLabel?: string;
}

const ToolbarButton: React.FC<{
  title: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ title, onClick, active, disabled = false, children, style = {} }) => {
  const { theme } = useTheme();
  return (
    <button
      title={title}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        backgroundColor: active ? theme.toolbar.buttonBackgroundActive : theme.toolbar.buttonBackground,
        color: theme.toolbar.buttonText,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: theme.toolbar.buttonBorder,
        borderRadius: 6,
        padding: '6px 8px',
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.65 : 1,
        fontSize: '14px',
        minWidth: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!active && !disabled) (e.currentTarget as HTMLElement).style.backgroundColor = theme.name === 'light' ? '#f1f5f9' : '#374151';
      }}
      onMouseLeave={(e) => {
        if (!active && !disabled) (e.currentTarget as HTMLElement).style.backgroundColor = theme.toolbar.buttonBackground;
      }}
    >
      {children}
    </button>
  );
};

const Divider: React.FC = () => {
  const { theme } = useTheme();
  return <div style={{ width: 1, height: 24, background: theme.toolbar.border, margin: '0 4px' }} />;
};

const ToolbarBcm: React.FC<ToolbarBcmProps> = ({
  onAdd,
  onSync,
  onExportPng,
  maps = [],
  selectedMapId,
  onSelectMap,
  onCreateMap,
  creatingMap = false,
  title = 'BCM',
  modeLabel = 'Registry-driven board',
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const iconStyle = { width: 18, height: 18 };

  return (
    <div style={{
      height: 48, zIndex: 2, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      backgroundColor: theme.name === 'light' ? '#ffffff' : '#111827',
      borderBottom: `1px solid ${theme.name === 'light' ? '#e2e8f0' : theme.toolbar.border}`,
      padding: '0 14px',
      boxShadow: 'none',
    }}>

      {/* Left section */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <ToolbarButton title="Назад" onClick={() => navigate(-1)}>
          <ArrowBackIcon style={iconStyle} />
        </ToolbarButton>
        <div style={{ fontSize: 17, fontWeight: 'bold', color: theme.toolbar.text, marginRight: 8 }}>
          {title}
        </div>
        {onAdd && (
          <ToolbarButton title="Добавить узел" onClick={onAdd}>
            <AddIcon style={iconStyle} />
          </ToolbarButton>
        )}
        {onSync && (
          <ToolbarButton title="Синхронизировать карту" onClick={onSync}>
            <SyncIcon style={iconStyle} />
          </ToolbarButton>
        )}
        {onExportPng && (
          <ToolbarButton title="Сохранить как PNG" onClick={onExportPng}>
            <DownloadIcon style={iconStyle} />
          </ToolbarButton>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {maps.length > 0 && (
          <select
            aria-label="Выбрать BCM карту"
            value={selectedMapId}
            onChange={(event) => onSelectMap?.(event.target.value)}
            style={{
              height: 32,
              maxWidth: 320,
              borderRadius: 6,
              border: `1px solid ${theme.toolbar.buttonBorder}`,
              background: theme.toolbar.buttonBackground,
              color: theme.toolbar.buttonText,
              padding: '0 8px',
              fontSize: 13,
              outline: 'none',
            }}
          >
            {maps.map((item) => (
              <option key={item.id} value={item.id}>
                {item.attributes?.auto_populated ? 'Default: ' : ''}
                {item.name}
              </option>
            ))}
          </select>
        )}
        {onCreateMap && (
          <ToolbarButton
            title="Создать свою BCM карту"
            onClick={onCreateMap}
            disabled={creatingMap}
          >
            <MapOutlinedIcon style={iconStyle} />
            <span style={{ fontSize: 13 }}>Своя карта</span>
          </ToolbarButton>
        )}
        <Divider />
        <div style={{ color: theme.toolbar.text, fontSize: 12, opacity: 0.7 }}>{modeLabel}</div>
      </div>
    </div>
  );
};

export default ToolbarBcm;
